/**
 * `LazyImage` — drop-in `<img>` replacement that:
 *   - Lazy-loads by default (`loading="lazy"`, `decoding="async"`). Pass
 *     `loading="eager"` for hero/LCP candidates (logo, lightbox photo,
 *     active slide in PhotoSlider, etc.).
 *   - Shows a muted skeleton (`animate-pulse bg-surface-2`) on the image
 *     element itself while bytes are in flight. The skeleton is rendered
 *     by the `<img>` background and is hidden as soon as the browser
 *     decodes the image (cache-hit images skip the skeleton entirely via
 *     the `complete` check on mount).
 *   - Falls through to the standard broken-image / alt-text rendering on
 *     network errors — no skeleton stays stuck forever.
 *
 * All standard `<img>` props pass through. The component intentionally
 * does NOT introduce a wrapper element so it can be swapped one-for-one
 * for any existing `<img>` without changing layout.
 */
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ImgHTMLAttributes,
} from 'react';

import { cn } from '@/lib/utils';

export interface LazyImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** `'lazy'` (default) or `'eager'` for hero / LCP candidates. */
  loading?: 'lazy' | 'eager';
  /**
   * Render the pulsing skeleton background while the image is loading.
   * Defaults to `true`. Set `false` for tiny brand marks / icons (Logo,
   * gateway logos) where the placeholder pulse would feel out of place.
   */
  skeleton?: boolean;
}

export const LazyImage = forwardRef<HTMLImageElement, LazyImageProps>(
  function LazyImage(
    { loading = 'lazy', skeleton = true, fetchPriority, className, onLoad, onError, ...rest },
    forwardedRef,
  ) {
    // For LCP-class images (`loading="eager"`) default the browser fetch
    // priority to `'high'` so the request is hoisted ahead of below-the-fold
    // images. Callers can still override explicitly via the prop.
    const effectiveFetchPriority =
      fetchPriority ?? (loading === 'eager' ? 'high' : undefined);
    const innerRef = useRef<HTMLImageElement | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [errored, setErrored] = useState(false);
    const hasSrc = Boolean(rest.src);

    // Cached / already-decoded images may finish before React attaches the
    // load listener (no `load` event fires after the listener is wired up
    // because the image is already complete). Check on mount AND on every
    // `src` change so swapped images (avatar updates, gallery navigation,
    // photo cycling, etc.) reset the loaded / errored state cleanly.
    useEffect(() => {
      setLoaded(false);
      setErrored(false);
      const img = innerRef.current;
      if (img && img.complete && img.naturalHeight > 0) {
        setLoaded(true);
      }
    }, [rest.src]);

    // React 18 hasn't yet adopted the camelCase `fetchPriority` prop name
    // (that landed in React 19); passing it directly triggers a "React does
    // not recognize the prop" warning. The lowercase HTML attribute
    // `fetchpriority` is still honoured by the browser, so spread it onto
    // the element via an object that bypasses the JSX/TS prop-name check.
    // Drop the spread entirely when no priority is set so we don't emit an
    // empty `fetchpriority=""` attribute.
    const fetchPriorityAttr: Record<string, string> = effectiveFetchPriority
      ? { fetchpriority: effectiveFetchPriority }
      : {};

    // `<picture>` + WebP `<source>` was added 2026-06-13 but rolled
    // back the same day after the server-side `cacheImage()`'s WebP
    // sibling didn't backfill legacy cache entries fast enough →
    // every legacy `/cache/.../foo.jpg` request produced a paired
    // `.webp` 404 in DevTools. The 404s aren't user-visible (browser
    // falls through to `<img>`) but they're noisy. Re-enable once
    // coverage > 90% (e.g. after a one-off backfill script walks
    // `storage/cache/` writing `.webp` twins for every existing
    // resize). The server's `cacheImage()` still writes WebP for new
    // resizes — coverage grows naturally; we just don't consume it
    // from the frontend yet.

    return (
      <img
        {...rest}
        {...fetchPriorityAttr}
        ref={(el) => {
          innerRef.current = el;
          if (typeof forwardedRef === 'function') forwardedRef(el);
          else if (forwardedRef) forwardedRef.current = el;
        }}
        loading={loading}
        decoding="async"
        onLoad={(e) => {
          setLoaded(true);
          onLoad?.(e);
        }}
        onError={(e) => {
          setErrored(true);
          onError?.(e);
        }}
        className={cn(
          className,
          // Skeleton: muted background + pulse while the bytes are in flight.
          // The browser paints the image over this once decoded, so no
          // transition flash is needed. Disabled for tiny brand marks AND
          // when there's no `src` (otherwise the pulse stays forever on an
          // empty box — `complete=true` + `naturalHeight=0` skips the
          // fast-path effect, and no `load` event ever fires).
          skeleton && hasSrc && !loaded && !errored && 'animate-pulse bg-surface-2',
        )}
      />
    );
  },
);
